import { useMemo, useState } from 'react';

import { Button, Checkbox, Empty, Skeleton } from '../../../../components/ui';
import { useT } from '../../../../i18n';
import { soVN } from '../../../../lib/dinh-dang';
import { useToast } from '../../../../lib/toast';
import { useLuuMaTranBot, useMaTranBot } from '../../hooks/useBot';
import { useVaiTroToChuc } from '../../hooks/useChung';
import {
  boDongDongThem,
  doiQuyenBot,
  giongNhau,
  gopDongQuyenBot,
  thayBot,
} from '../../nghiep-vu/cau-hinh-bot';
import type { BotGrant } from '../../types';
import { DaiLoi } from '../chung/VungDuLieu';
import { useMoTaLoi } from '../../hooks/useMoTaLoi';
import { Khoi } from './chung';

/**
 * Tab "Quyền truy cập" — vai nào dùng được bot NÀY. Cùng ma trận `chatbot_grants` với "vai này thấy bot nào", nhìn theo
 * chiều ngược lại.
 *
 * Lưu RIÊNG, không qua nút Lưu cấu hình bot: đây là quyền, không phải cấu hình, và có hiệu lực cả khi bot chưa bật.
 *
 * Khác native: bỏ trục phòng ban (`PhongBanDangApDung`, ô phạm vi) — platform không có phòng ban. Danh sách vai lấy từ
 * API vai trò của platform gộp với dòng ma trận máy chủ đã có; vai chưa có dòng hiện ở trạng thái chưa tích.
 */
export function TabQuyen({ botId, suaDuoc }: { botId: string | null; suaDuoc: boolean }) {
  const { t, locale } = useT();
  const toast = useToast();
  const moTaLoi = useMoTaLoi();
  const maTran = useMaTranBot(!!botId);
  const vaiToChuc = useVaiTroToChuc(!!botId);
  const luu = useLuuMaTranBot();
  // `null` = chưa sửa gì, hiện đúng bản máy chủ. Không chép bản máy chủ vào state bằng effect: lưu xong chỉ cần về `null`.
  const [nhap, setNhap] = useState<BotGrant[] | null>(null);

  const goc = useMemo(
    () => gopDongQuyenBot(maTran.data?.grants ?? [], (vaiToChuc.data ?? []).map((v) => v.ma)),
    [maTran.data, vaiToChuc.data],
  );
  const tenVai = useMemo(() => {
    const m = new Map((vaiToChuc.data ?? []).map((v) => [v.ma, v.ten]));
    for (const v of maTran.data?.vai ?? []) m.set(v.role, v.ten);
    return m;
  }, [maTran.data, vaiToChuc.data]);
  const moiBot = useMemo(() => (maTran.data?.chatbot ?? []).map((b) => b.id), [maTran.data]);

  if (!botId) {
    return <Empty title={t('troLyAiBotQuyenTruyCap.chuaTao')} description={t('troLyAiBotQuyenTruyCap.chuaTaoMoTa')} />;
  }
  if (maTran.error) return <DaiLoi loi={maTran.error} onThuLai={() => void maTran.refetch()} />;
  if (!maTran.data) return <Skeleton className="h-40 w-full" />;

  const ds = nhap ?? goc;
  const guiLen = boDongDongThem(ds, maTran.data.grants);
  const coDoi = !giongNhau(guiLen, maTran.data.grants);
  // Bot vừa tạo có thể chưa có trong danh sách bot của ma trận — vẫn phải tích được cho nó.
  const tatCaBot = moiBot.includes(botId) ? moiBot : [...moiBot, botId];
  const soThay = ds.filter((g) => thayBot(g, botId)).length;

  return (
    <div className="space-y-4">
      <Khoi ten={t('troLyAiBotQuyenTruyCap.tieuDe')} mo={t('troLyAiBotQuyenTruyCap.luuRieng')}>
        {ds.length === 0 ? (
          <p className="text-xs text-content-muted">{t('troLyAiBotQuyenTruyCap.khongCoVai')}</p>
        ) : (
          <>
            <p className="text-xs text-content-muted">
              {t('troLyAiBotQuyenTruyCap.dem', { n: soVN(soThay, locale), tong: soVN(ds.length, locale) })}
            </p>
            <ul className="grid gap-1.5 sm:grid-cols-2">
              {ds.map((g) => (
                <li key={g.role} className="min-w-0 rounded px-2 py-1.5 hover:bg-subtle">
                  <Checkbox
                    checked={thayBot(g, botId)}
                    disabled={!suaDuoc || luu.isPending}
                    onChange={(e) =>
                      setNhap(ds.map((x) => (x.role === g.role ? doiQuyenBot(x, botId, e.target.checked, tatCaBot) : x)))
                    }
                  >
                    <span className="text-sm">{tenVai.get(g.role) ?? g.role}</span>
                    <span className="ms-1.5 font-mono text-xs text-content-muted">{g.role}</span>
                  </Checkbox>
                </li>
              ))}
            </ul>
          </>
        )}
        <p className="text-xs leading-relaxed text-content-muted">{t('troLyAiBotQuyenTruyCap.giaiThich')}</p>
      </Khoi>
      {suaDuoc ? (
        <div className="flex flex-wrap justify-end gap-2">
          <Button size="sm" variant="ghost" disabled={!coDoi || luu.isPending} onClick={() => setNhap(null)}>
            {t('troLyAiBotQuyenTruyCap.hoanTac')}
          </Button>
          <Button
            size="sm"
            variant="primary"
            disabled={!coDoi}
            loading={luu.isPending}
            onClick={() =>
              luu.mutate(guiLen, {
                onSuccess: () => {
                  toast.success(t('troLyAiBotQuyenTruyCap.daLuu'));
                  setNhap(null);
                },
                onError: (e) => toast.error(moTaLoi(e)),
              })
            }
          >
            {t('troLyAiBotQuyenTruyCap.luu')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
